import { EmployeeInvitationStatusTypes, SubscriberAddOnMethod, SubscribersFilter } from 'types/electionService';
import { getSubscribersPageForGroup } from 'api/operations/electionService';
import { applyProposalUserAction } from 'api/operations/enrollmentService';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { bpHistory } from 'global/bpHistory';
import { FiltersObject } from 'store/general/paginationDelegate';
import IAddEmployeesPage from 'store/modules/add-employees/iAddEmployeesPage';
import { USER_ACTIONS } from 'types/enrollmentService';
import { getStandardActionsWithSelfEnrollFeature } from 'store/modules/add-employees/inviteEmployeeActions';

interface InitiateAddEmployeesDomainParams {
  groupId: string;
  periodId: string;
  cartId: string;
}

interface InitiateAddEmployeesFilters extends FiltersObject {
  status: EmployeeInvitationStatusTypes | null;
  addOnMethod: SubscriberAddOnMethod | null;
  employeeName: string;
}

export default class InitiateAddEmployeesPageStore extends IAddEmployeesPage<InitiateAddEmployeesFilters> {
  protected _domainParams: InitiateAddEmployeesDomainParams;

  public get stepCanComplete(): boolean {
    return this.employees.length > 0;
  }

  public async getEmployees(): Promise<void> {
    this._setIsLoading(true);
    const { result, totalElements, totalPages } = await getSubscribersPageForGroup({
      groupId: this._domainParams.groupId,
      sortBy: this.paginationDelegate.sortBy,
      sortOrder: this.paginationDelegate.sortOrder,
      status: this.paginationDelegate.filters.status ? [this.paginationDelegate.filters.status] : [],
      addOnMethod: this.paginationDelegate.filters.addOnMethod ? [this.paginationDelegate.filters.addOnMethod] : [],
      progress: [],
      filter: SubscribersFilter.ALL,
      name: this.paginationDelegate.filters.employeeName,
      page: this.paginationDelegate.page,
    });
    runInAction(() => {
      this._createEmployeeDelegates(result);
      this.paginationDelegate.setTotalElements(totalElements);
      this.paginationDelegate.setTotalPages(totalPages);
      this._setIsLoading(false);
    });
  }

  public async onComplete(): Promise<void> {
    this._setIsLoading(true);
    await applyProposalUserAction(
      this._domainParams.periodId,
      this._domainParams.cartId,
      USER_ACTIONS.COMPLETE_ADD_EMPLOYEES,
    );
    runInAction(() => {
      this._setIsLoading(false);
      this.onBack();
    });
  }

  public onBack(): void {
    bpHistory.push(`/rfq-checklist/${this._domainParams.periodId}?cartId=${this._domainParams.cartId}`);
  }

  constructor(params: InitiateAddEmployeesDomainParams) {
    super({ status: null, employeeName: '', addOnMethod: null }, getStandardActionsWithSelfEnrollFeature(), params);
    this._domainParams = params;

    makeObservable<InitiateAddEmployeesPageStore, '_domainParams'>(this, {
      _domainParams: observable,
      stepCanComplete: computed,
      getEmployees: action.bound,
      onComplete: action.bound,
      onBack: action.bound,
    });
  }
}

const InitiateAddEmployeesPageStoreContext = createContext(
  new InitiateAddEmployeesPageStore({
    groupId: 'stub-group-id',
    cartId: 'stub-cart-id',
    periodId: 'stub-enrollment-id',
  }),
);

export const useInitiateAddEmployeesPageStore = (): InitiateAddEmployeesPageStore => {
  return useContext(InitiateAddEmployeesPageStoreContext);
};

export { InitiateAddEmployeesPageStore, InitiateAddEmployeesPageStoreContext };
